import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import get from 'lodash/get';
import { round } from 'utils/number';
import { DisplayHelpers } from 'utils/helpers';
import {
  getInheritedSettings,
  getClassificationFromSource,
  calculateNewTotalMarginPercentW2,
  resolveW2BurdenMultipliers,
} from '../PositionRateSettingsForm.utils';
import {
  CONTROLLABLE_CODES,
  CONTROLLED_RATE_FIELDS_KEY,
  CONTROLLER_WRITTEN_FIELDS,
  DEFAULT_CONTROLLED,
  RATE_CONTROLLER_ENGAGED_KEY,
  baseMarginField,
  codeToField,
  deriveBlendedMarkup,
  deriveInheritedRatePreview,
  isBlank,
  markupFactorOf,
  num,
  resolveRateSolveConfig,
  solveRates,
  swapControlled,
  toBlendedOverride,
  type RateCode,
  type RateValues,
} from './rateControllerModel';
import { seedRateDefaults } from './rateControllerDefaults';
import {
  calculatedFieldPath,
  useRecordValueReader,
} from 'resources/rate_settings/utils/fieldsVisibility';
import type { RateControllerValue } from './RateControllerContext';

type UseRateControllerProps = {
  source: string;
  inheritedSettings: any;
  resource?: string;
};

const toCents = (value: string | number): number =>
  typeof value === 'string' ? DisplayHelpers.stringToCents(value) : value;

const getInheritedUseBlended = (inheritedSettings: any, tab: string): boolean => {
  const inheritedTabHash =
    inheritedSettings?.ten99 || inheritedSettings?.w2
      ? get(inheritedSettings, tab)
      : inheritedSettings;
  return Boolean(inheritedTabHash?.use_blended_rate);
};

export const useRateController = ({
  source,
  inheritedSettings,
  resource,
}: UseRateControllerProps): RateControllerValue => {
  const { watch, getValues, setValue, resetField, getFieldState } = useFormContext();
  const recordValue = useRecordValueReader();
  const controlledKey = `${source}.${CONTROLLED_RATE_FIELDS_KEY}`;
  const engagedKey = `${source}.${RATE_CONTROLLER_ENGAGED_KEY}`;
  const { level, tab } = getClassificationFromSource(source);
  const isLPRS = resource === 'LocationPositionRateSetting';
  // Location level is 8hr-anchored: the blended markup never folds into the
  // Location Rate, so base and Location Rate differ only by the charge markups.
  const isLocationLevel = isLPRS || resource === 'AgencyLocationPositionRateSetting';
  const isW2 = source.includes('w2');

  // Seeding is a first-render default only: once the admin has interacted and
  // cleared back to nothing (disengage), erased fields must stay erased.
  const suppressSeedRef = useRef(false);

  const watchedControlled = watch(controlledKey);
  const controlled: RateCode[] = useMemo(
    () =>
      Array.isArray(watchedControlled) && watchedControlled.length === 2
        ? (watchedControlled as RateCode[])
        : DEFAULT_CONTROLLED,
    [watchedControlled],
  );

  const recompute = useCallback(() => {
    // Ensure the selection is initialized (reactive to form resets, e.g. react-admin).
    const currentControlled = getValues(controlledKey);
    if (!Array.isArray(currentControlled) || currentControlled.length !== 2) {
      setValue(controlledKey, DEFAULT_CONTROLLED, { shouldDirty: false });
    }

    const merged = getInheritedSettings({ source, inheritedSettings, watch });
    const marginPath = baseMarginField(source);
    const isW2Local = tab === 'w2.local';
    const config = resolveRateSolveConfig({
      merged,
      isW2,
      isW2Local,
      isLocationLevel,
      burdenMultipliersSum: isW2Local ? resolveW2BurdenMultipliers(watch) : 0,
    });
    const { markups, rawBlendedMultiplier, blendedFactor, burdenDivisor } = config;

    if (getValues(engagedKey) !== true) {
      if (!suppressSeedRef.current) {
        seedRateDefaults({ source, recordValue, getValues, setValue, config });
      }
      return;
    }

    // Resolved settings never carry pro_rate_cents — blank fields fall back to the preview.
    const inheritedTabHash =
      inheritedSettings?.ten99 || inheritedSettings?.w2
        ? get(inheritedSettings, tab)
        : inheritedSettings;
    const preview = deriveInheritedRatePreview({
      inherited: inheritedTabHash,
      source,
      isLocationLevel,
      useBlendedOverride: toBlendedOverride(getValues(`${source}.use_blended_rate`)),
    });

    // charge_rate_cents lives under the settings namespace (calculatedFieldPath),
    // outside the own-blob hash that getInheritedSettings merges — read the live
    // form value first so admin edits are seen, then fall back to the merge.
    const liveChargeRate = getValues(codeToField('chargeRate', source, recordValue));
    // The inherited base at location level must come from the preview: it folds the
    // location_type_markup in (zeroed by the backend once an own base is saved).
    const ownBaseChargeRate = getValues(`${source}.base_charge_rate_cents`);
    const values: RateValues = {
      professionalRate: isBlank(merged.pro_rate_cents)
        ? num(preview.professionalRate)
        : num(merged.pro_rate_cents),
      baseMargin: num(merged?.[marginPath.split('.').pop() as string]),
      baseChargeRate: !isBlank(ownBaseChargeRate)
        ? num(ownBaseChargeRate)
        : isLocationLevel && preview.baseChargeRate !== null
        ? num(preview.baseChargeRate)
        : num(merged.base_charge_rate_cents),
      chargeRate: !isBlank(liveChargeRate)
        ? num(liveChargeRate)
        : isLocationLevel && preview.chargeRate !== null
        ? num(preview.chargeRate)
        : num(merged.charge_rate_cents),
    };

    const solved = solveRates({ controlled, values, markups, blendedFactor, burdenDivisor });

    const writeIfChanged = (path: string, next: number) => {
      if (!Number.isFinite(next)) {
        return;
      }
      // Write when the field is empty (so a legitimate 0 fills a null) or when
      // its numeric value differs. num() keeps string-typed margins (e.g. "0.35")
      // from churning against their numeric equivalent. shouldDirty stays false:
      // these are system-computed refreshes, not admin edits — a pristine form
      // must not become saveable (or persist inherited snapshots) just by mounting.
      const current = getValues(path);
      if (isBlank(current) || num(current) !== next) {
        setValue(path, next, { shouldDirty: false });
      }
    };

    const computed = CONTROLLABLE_CODES.filter((code) => !controlled.includes(code));
    computed.forEach((code) => {
      const path = codeToField(code, source, recordValue);
      if (code === 'chargeRate' || code === 'professionalRate') {
        writeIfChanged(path, Math.round(solved[code]));
      } else if (code === 'baseChargeRate') {
        writeIfChanged(path, round(solved.baseChargeRate, 4));
      } else {
        writeIfChanged(path, round(solved.baseMargin, 5));
      }
    });

    const chargeMarkupFactor = markupFactorOf(markups);
    const finalMarginOf = (baseMargin: number) => {
      return round(1 - (1 - baseMargin) / chargeMarkupFactor, 5);
    };

    const hasBaseMargin = controlled.includes('baseMargin')
      ? !isBlank(merged?.[marginPath.split('.').pop() as string])
      : Number.isFinite(solved.baseMargin);

    if (tab === 'ten99' && hasBaseMargin) {
      writeIfChanged(
        calculatedFieldPath(source, 'margin_percent_1099', recordValue),
        finalMarginOf(solved.baseMargin),
      );
    } else if (tab === 'w2.local') {
      calculateNewTotalMarginPercentW2({
        chargeRate: solved.chargeRate * blendedFactor,
        professionalRate: solved.professionalRate,
        setValue,
        source,
        watch,
        getRecordValue: recordValue,
        solved,
      });
    } else if (tab === 'w2.travel') {
      if (hasBaseMargin) {
        writeIfChanged(
          calculatedFieldPath(source, 'margin_percent_w2_travel', recordValue),
          round(solved.baseMargin, 5),
        );
      }
      // TCV mirrors the travel margin but from its own (uncontrolled) base field.
      const baseMarginTcv = merged?.base_margin_percent_w2_tcv;
      if (!isBlank(baseMarginTcv)) {
        writeIfChanged(
          calculatedFieldPath(source, 'margin_percent_w2_tcv', recordValue),
          finalMarginOf(num(baseMarginTcv)),
        );
      }
    }

    // Blank/NaN means "no markup configured anywhere" — leave blended_rate_cents
    // alone. An explicit 0 is a real value and must sync blended to the charge rate.
    if (!isBlank(rawBlendedMultiplier) && Number.isFinite(Number(rawBlendedMultiplier))) {
      writeIfChanged(
        `${source}.blended_rate_cents`,
        Math.round(solved.chargeRate * (1 + num(rawBlendedMultiplier))),
      );
    }

    if (controlled.includes('chargeRate') && isLPRS) {
      const markupFields = [
        'invoicing_markup',
        'misc_markup',
        ...(isW2 ? ['operational_markup'] : []),
      ];
      markupFields.forEach((mf) => {
        if (isBlank(getValues(`${source}.${mf}`)) && !isBlank(merged?.[mf])) {
          setValue(`${source}.${mf}`, num(merged[mf]), { shouldDirty: false });
        }
      });
    }
  }, [
    controlled,
    source,
    tab,
    isLPRS,
    isLocationLevel,
    isW2,
    inheritedSettings,
    watch,
    getValues,
    setValue,
    recordValue,
    controlledKey,
    engagedKey,
  ]);

  useEffect(() => {
    const timeout = setTimeout(recompute, 0);
    return () => clearTimeout(timeout);
  }, [recompute]);

  useEffect(() => {
    const triggers = new Set<string>([
      ...controlled.map((c) => codeToField(c, source, recordValue)),
      `${source}.invoicing_markup`,
      `${source}.misc_markup`,
      `${source}.operational_markup`,
      `${source}.blended_rates.blended_rate_markup`,
      `${source}.base_margin_percent_w2_tcv`,
    ]);
    const adminInputFields = [
      ...CONTROLLABLE_CODES.map((code) => codeToField(code, source, recordValue)),
      `${source}.invoicing_markup`,
      `${source}.misc_markup`,
      `${source}.operational_markup`,
      `${source}.blended_rates.blended_rate_markup`,
      `${source}.base_margin_percent_w2_tcv`,
    ];
    const sub = watch((_, { name, type }) => {
      if (!name || !triggers.has(name)) {
        return;
      }
      if (type === 'change') {
        // Any admin edit on the rate cluster — typing or erasing — ends the
        // first-render seeding; erasing a seeded value must leave it erased.
        suppressSeedRef.current = true;
        const hasAdminInput = adminInputFields.some(
          (path) => !isBlank(getValues(path)) && getFieldState(path).isDirty,
        );
        if (hasAdminInput) {
          if (getValues(engagedKey) !== true) {
            setValue(engagedKey, true, { shouldDirty: false });
          }
        } else if (getValues(engagedKey) === true) {
          setValue(engagedKey, false, { shouldDirty: false });
          CONTROLLER_WRITTEN_FIELDS.forEach((field) =>
            resetField(calculatedFieldPath(source, field, recordValue)),
          );
          // disengage restores the pristine record state — let recompute re-seed
          suppressSeedRef.current = false;
        }
      }
      recompute();
    });
    return () => sub.unsubscribe();
  }, [
    watch,
    controlled,
    source,
    recompute,
    engagedKey,
    getValues,
    setValue,
    resetField,
    getFieldState,
    recordValue,
  ]);

  const isControlled = useCallback((code: RateCode) => controlled.includes(code), [controlled]);

  const toggle = useCallback(
    (code: RateCode) => {
      const updated = swapControlled(controlled, code);
      if (updated !== controlled) {
        setValue(controlledKey, updated, { shouldDirty: false });
      }
    },
    [controlled, controlledKey, setValue],
  );

  const recalcProfessionalRateForStructure = useCallback(
    (
      targetSource: string,
      nextOverride: boolean | undefined,
      previousOverride: boolean | undefined,
    ) => {
      if (isLocationLevel) {
        return;
      }
      const { tab: targetTab } = getClassificationFromSource(targetSource);
      const merged = getInheritedSettings({ source: targetSource, inheritedSettings, watch });

      const inheritedUseBlended = getInheritedUseBlended(inheritedSettings, targetTab);
      const prevEffective = previousOverride ?? inheritedUseBlended;
      const nextEffective = nextOverride ?? inheritedUseBlended;

      const proRatePath = codeToField('professionalRate', targetSource, recordValue);

      const liveProRate = getValues(proRatePath);
      if (isBlank(liveProRate) || (!isBlank(liveProRate) && prevEffective === nextEffective)) {
        return;
      }

      const blendedModifier = num(merged?.blended_rates?.blended_rate_markup);
      const blendedFactor = 1 + blendedModifier;
      if (blendedFactor <= 0) {
        return;
      }

      if (!isBlank(liveProRate)) {
        const baseValue = num(liveProRate);
        if (!prevEffective && nextEffective) {
          setValue(proRatePath, baseValue * blendedFactor, { shouldDirty: false });
        } else if (prevEffective && !nextEffective) {
          setValue(proRatePath, baseValue / blendedFactor, { shouldDirty: false });
        }
        return;
      }
    },
    [getValues, inheritedSettings, isLocationLevel, recordValue, setValue, watch],
  );

  const setRateStructure = useCallback(
    (useBlended: boolean | undefined) => {
      const prevOverride = toBlendedOverride(getValues(`${source}.use_blended_rate`));
      setValue(`${source}.use_blended_rate`, useBlended, { shouldDirty: true });
      recalcProfessionalRateForStructure(source, useBlended, prevOverride);

      if (isW2) {
        const sibling = tab === 'w2.local' ? 'travel' : 'local';
        const siblingSource = `${level}.w2.${sibling}`;
        const siblingPrevOverride = toBlendedOverride(
          getValues(`${siblingSource}.use_blended_rate`),
        );

        setValue(`${siblingSource}.use_blended_rate`, useBlended, { shouldDirty: true });
        recalcProfessionalRateForStructure(siblingSource, useBlended, siblingPrevOverride);
      }
    },
    [getValues, isW2, level, recalcProfessionalRateForStructure, setValue, source, tab],
  );

  const setBlendedRate = useCallback(
    (value: string | number) => {
      const merged = getInheritedSettings({ source, inheritedSettings, watch });
      const chargeCents =
        num(getValues(codeToField('chargeRate', source, recordValue))) ||
        num(merged.charge_rate_cents);
      const markup = deriveBlendedMarkup(toCents(value), chargeCents);
      if (markup === null) {
        return;
      }
      setValue(engagedKey, true, { shouldDirty: false });
      setValue(`${source}.blended_rates.blended_rate_markup`, markup, { shouldDirty: true });
    },
    [source, inheritedSettings, watch, getValues, setValue, engagedKey, recordValue],
  );

  const marginVal = num(watch(baseMarginField(source)));
  const chargeRateMarkups = [
    num(watch(`${source}.invoicing_markup`)),
    num(watch(`${source}.misc_markup`)),
    isW2 ? num(watch(`${source}.operational_markup`)) : 0,
  ];
  const hasInvalidMarkup = chargeRateMarkups.some((m) => 1 + m <= 0);

  const warnings = useMemo<string[]>(() => {
    const msgs: string[] = [];
    if (marginVal >= 1) {
      msgs.push('Base Margin must be less than 1.');
    }
    if (hasInvalidMarkup) {
      msgs.push('Each markup must be greater than −1.');
    }
    return msgs;
  }, [marginVal, hasInvalidMarkup]);

  return { controlled, isControlled, toggle, setRateStructure, setBlendedRate, warnings };
};
